/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiFieldText,
  EuiFieldPassword,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiLink,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import history from '@modules/utils/history'
import { CuiAlert } from '@modules/cui/Alert'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import { submitButtonStyle } from '@modules/access-management-components/styles'
import AuthContext from '@modules/auth/context'
import { acceptInviteUrl } from '@modules/auth/urls'

import { initializeAuthForm } from '@/lib/authForm'
import { getConfigForKey } from '@/store'
import { forgotPasswordUrl } from '@/lib/urlBuilder'
import OAuthLoginError from '@/components/Login/OAuthLoginError'
import { buildSignInLink, buildSignInQuery } from '@/lib/urlUtils'
import SsoSignUp from '@/components/CreateAccountForm/SsoSignUp'

import GoogleSignUp from '../../../../CreateAccountForm/GoogleSignUp'
import MicrosoftSignUp from '../../../../CreateAccountForm/MicrosoftSignUp'
import SpinButton from '../../../../SpinButton'

import type { Location } from 'history'
import type { FC, ReactElement, ReactNode, SyntheticEvent } from 'react'

export type Props = {
  location: Location<any>
  error: ReactNode | null
  username: string
  password: string
  updateUsername
  updatePassword
  onSubmit: (e: SyntheticEvent) => void
  inProgress: boolean
  registrationButtons: boolean
  openIdRedirectUrl?: string
  registerUrl?: string
  isGovCloud?: boolean
  hasOAuthLoginError?: boolean
  ssoLoginIdentifier?: string
}

const fieldsText = {
  usernameText: {
    id: 'login.username-text',
    defaultMessage: 'Username',
  },
  emailText: {
    id: 'login.email-text',
    defaultMessage: 'Email',
  },
  passwordText: {
    id: 'login.password-text',
    defaultMessage: 'Password',
  },
}

const LoginForm: FC<Props> = ({
  location,
  error,
  username,
  password,
  updateUsername,
  updatePassword,
  onSubmit,
  inProgress,
  registrationButtons,
  openIdRedirectUrl,
  registerUrl,
  isGovCloud,
  hasOAuthLoginError,
  ssoLoginIdentifier,
  location: { pathname },
}) => {
  const authContext = useContext(AuthContext)
  const { formatMessage } = useIntl()
  const [hideOauthLoginError, setHideOauthLoginError] = useState(false)
  const [isSubmittable, setIsSubmittable] = useState(false)
  const formRef = useRef<HTMLFormElement | null>(null)

  useEffect(() => {
    initializeAuthForm({
      form: formRef.current,
      onFormReady: (autoFillDetected) => {
        setIsSubmittable(autoFillDetected)
      },
    })
  }, [])

  useEffect(() => {
    const isValid = !!username.trim() && !!password.trim()
    setIsSubmittable(isValid)
  }, [username, password])

  const isEce = getConfigForKey(`CLOUD_UI_APP`) === `cloud-enterprise-adminconsole`
  const isEssUserconsole =
    getConfigForKey(`APP_PLATFORM`) === `saas` && getConfigForKey(`APP_NAME`) === `userconsole`
  const usernameText = isEce ? fieldsText.usernameText : fieldsText.emailText

  const usernamePlaceholder = isEssUserconsole ? usernameText.defaultMessage : ''
  const passwordPlaceholder = isEssUserconsole ? fieldsText.passwordText.defaultMessage : ''

  // If the user is coming from an invitation link and there is no ssoLoginIdentifier, we disable the SSO button
  const isFromInvitation = pathname.startsWith(acceptInviteUrl())
  const isSsoDisabled = isFromInvitation && !ssoLoginIdentifier

  const showPasswordlessRegistrationButtons =
    registrationButtons && registerUrl && !(isGovCloud && isSsoDisabled)

  const getAriaLabel = () => {
    if (showPasswordlessRegistrationButtons) {
      if (!isSsoDisabled) {
        return formatMessage({
          id: 'login-form-password.aria-label',
          defaultMessage: 'Login with email, Google, Microsoft, or SSO',
        })
      }

      return formatMessage({
        id: 'login-form-password.aria-label-no-sso',
        defaultMessage: 'Login with email, Google, or Microsoft',
      })
    }

    return undefined
  }

  const handleSubmit = (e: SyntheticEvent) => {
    if (hasOAuthLoginError) {
      setHideOauthLoginError(true)
    }

    authContext.setAuthMethod('username-and-password')

    onSubmit(e)
  }

  const renderForgotPassword = () => {
    const query = buildSignInQuery({ search: location.search, withReferrer: false })
    const forgotPasswordLink = buildSignInLink(forgotPasswordUrl(), query)

    if (registrationButtons) {
      return (
        <Fragment>
          <EuiSpacer />

          <EuiText
            textAlign='center'
            size='s'
            css={{
              textAlign: 'center',
            }}
            className='forgotPasswordLink'
          >
            <EuiLink
              href={forgotPasswordLink}
              onClick={(e) => {
                e.preventDefault()
                history.push(forgotPasswordLink)
              }}
              data-test-id='forgot-password-link'
            >
              <FormattedMessage id='login-form.forgot-password' defaultMessage='Forgot password?' />
            </EuiLink>
          </EuiText>
        </Fragment>
      )
    }

    return null
  }

  const renderError = (): ReactNode | null => {
    if (!error && !hasOAuthLoginError) {
      return null
    }

    if (hasOAuthLoginError) {
      const oAuthLoginError = location.state?.oAuthLoginError

      return (
        <Fragment>
          {error && renderErrorAlert(error)}

          <OAuthLoginError oAuthLoginError={oAuthLoginError} hidden={hideOauthLoginError} />
        </Fragment>
      )
    }

    return renderErrorAlert(error)
  }

  const renderErrorAlert = (errorContent): ReactElement => (
    <Fragment>
      <EuiSpacer />

      <CuiAlert data-test-id='login-error' type='error'>
        {errorContent}
      </CuiAlert>
    </Fragment>
  )

  return (
    <form
      onSubmit={handleSubmit}
      className='login-form-password'
      data-test-id='login-form-password'
      ref={formRef}
      aria-label={getAriaLabel()}
    >
      <PrivacySensitiveContainer>
        <EuiFormRow
          label={!isEssUserconsole && <FormattedMessage {...usernameText} />}
          className='login-form-email'
          data-test-id='username-form-row'
        >
          <EuiFieldText
            data-test-id='login-username'
            icon={isEssUserconsole ? 'email' : 'user'}
            value={username}
            onChange={updateUsername}
            name='email'
            placeholder={usernamePlaceholder}
            aria-label={usernamePlaceholder}
            fullWidth={true}
          />
        </EuiFormRow>

        <EuiSpacer size='m' />

        <EuiFormRow
          className='login-form-password-field'
          label={!isEssUserconsole && <FormattedMessage {...fieldsText.passwordText} />}
        >
          <EuiFieldPassword
            data-test-id='login-password'
            value={password}
            onChange={updatePassword}
            type='dual'
            name='password'
            placeholder={passwordPlaceholder}
            aria-label={passwordPlaceholder}
            fullWidth={true}
          />
        </EuiFormRow>
      </PrivacySensitiveContainer>

      <EuiSpacer size='m' />

      <SpinButton
        type='submit'
        data-test-id='login-button'
        css={submitButtonStyle}
        color={error ? `danger` : `primary`}
        fill={true}
        spin={inProgress}
        disabled={!isSubmittable}
        buttonProps={{ fullWidth: true }}
      >
        <FormattedMessage id='login-login-form.log-in' defaultMessage='Log in' />
      </SpinButton>

      {renderError()}

      {showPasswordlessRegistrationButtons && (
        <Fragment>
          <EuiSpacer size='m' />

          <EuiFlexGroup alignItems='center' gutterSize='s'>
            {!isGovCloud && (
              <Fragment>
                <EuiFlexItem>
                  <GoogleSignUp fullText={false} redirectTo={openIdRedirectUrl} />
                </EuiFlexItem>
                <EuiFlexItem>
                  <MicrosoftSignUp fullText={false} redirectTo={openIdRedirectUrl} />
                </EuiFlexItem>
              </Fragment>
            )}

            {!isSsoDisabled && (
              <EuiFlexItem>
                <SsoSignUp ssoLoginIdentifier={ssoLoginIdentifier} />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </Fragment>
      )}

      {renderForgotPassword()}
    </form>
  )
}

export default LoginForm
